import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {memo, useCallback} from 'react'

import {ThreadHeader} from './ThreadHeader'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {PreviewAuthors} from './PreviewAuthors'
import {useGetThread, useMarkerAndDiffNavigationActions} from '../../stores/selectors'
import {extractCommentAuthors} from './PendingCommentPreview'
import {addUrlToHistoryStack} from '@github-ui/history'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../page-data/loaders/use-thread-previews'

function panelThreadCollapsedStateStorageKey(id: string) {
  return `panel-thread-collapsed-state-${id}`
}

type ThreadPreviewProps = {
  onNavigateToDiffComment: () => void
  tabSize?: number
  thread: Thread
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

/**
 * Renders a preview of a thread in a pull request.
 * It displays the thread's header, the difflines that the
 * thread is associated with, and the first comment of the thread.
 */
export const ThreadPreview = memo(function ThreadPreview({
  onNavigateToDiffComment,
  tabSize,
  thread,
  headingLevel,
}: ThreadPreviewProps) {
  const firstComment = getFirstComment(thread)
  const commentId = firstComment?.id
  const {navigateToSelectedComment} = useMarkerAndDiffNavigationActions()

  const getThreadFromStore = useGetThread()

  const {filePath, lineKey} = getThreadPreviewsLineAndFileKeysFromThread(thread)
  const storedThread = getThreadFromStore(Number(thread.id), filePath, lineKey)
  //TODO: if the stored thread is undefined, then add the thread to the store
  const isResolved = storedThread?.isResolved || thread.isResolved || false
  const isOutdated = thread.isOutdated || thread.positioning?.isOutdated || false

  const navigateToComment = useCallback(() => {
    const databaseId = firstComment?.databaseId
    if (!databaseId) return

    if (isOutdated && thread.originalDiffPathUri) {
      // eslint-disable-next-line no-restricted-syntax
      const url = new URL(thread.originalDiffPathUri)
      url.searchParams.set('new_files_changed', 'true')

      window.location.href = url.toString()
    } else {
      navigateToSelectedComment(databaseId)
      const currentUrl = window.location.pathname + window.location.search
      addUrlToHistoryStack(`${currentUrl}#r${databaseId}`)
    }
    window.requestAnimationFrame(() => {
      onNavigateToDiffComment()
    })
  }, [firstComment, isOutdated, thread.originalDiffPathUri, navigateToSelectedComment, onNavigateToDiffComment])

  const [isCollapsed, setIsCollapsed] = useLocalStorage(panelThreadCollapsedStateStorageKey(thread.id), false)

  const threadComments = storedThread?.commentsData ?? thread.commentsData ?? {comments: []}
  // subtract 1 because the first comment is shown in the preview
  const replyCount = threadComments ? threadComments.comments.length - 1 : 0
  const repliesText = replyCount === 0 ? 'No replies' : `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`
  // The StaticUnifiedDiffPreview only looks at the subject, should probably by updated to only require that but for now
  // clean the comments so we don't have to match the typing by requesting a ton more data.
  const threadDiffPreview: typeof storedThread & {commentsData: {comments: Comment[]}} = {
    ...storedThread,
    id: storedThread?.id ?? thread.id,
    commentsData: {comments: []},
  }

  // if the comment returned is a reply, this will be true
  if (!firstComment || !threadDiffPreview) return null

  const authors = extractCommentAuthors(firstComment.id, threadComments)

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ThreadHeader
        firstCommentId={firstComment?.databaseId}
        isCollapsed={isCollapsed}
        thread={thread}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(!isCollapsed)}
        headingLevel={headingLevel}
        isResolved={isResolved}
        isOutdated={isOutdated}
        lineKey={lineKey}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={thread.subject?.diffLines}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          <div>
            <ReviewThreadComment
              lineMarkersKey={lineKey}
              key={commentId}
              hideActions
              isInlineComment={false}
              comment={firstComment}
              filePath={filePath}
              index={0}
              isAnchorable={false}
              isOutdated={isOutdated}
              isThreadResolved={isResolved}
              threadId={thread.id}
              originalDiffPathUri={thread.originalDiffPathUri}
              headingLevel="h3"
              tooltipDirection="sw"
            />
          </div>
          <div className="mb-2 px-2">
            <Button
              as="a"
              href={
                isOutdated && thread.originalDiffPathUri ? thread.originalDiffPathUri : `#r${firstComment?.databaseId}`
              }
              aria-label={`View ${repliesText}`}
              size="small"
              trailingVisual={ChevronRightIcon}
              variant="invisible"
              onClick={navigateToComment}
            >
              <div className="d-flex flex-row flex-justify-start flex-items-center gap-2">
                <span>{repliesText}</span>
                <PreviewAuthors commentAuthors={authors} />
              </div>
            </Button>
          </div>
        </>
      )}
    </div>
  )
})
